/**
 * Export React Flow canvas to raster/vector image formats using html-to-image.
 *
 * Strategy:
 *  1. Calculate the bounding box of every visible node (passed from the Zustand store).
 *  2. Build a CSS transform that fits all nodes into the chosen output dimensions.
 *  3. Apply that transform to the `.react-flow__viewport` element (DOM query).
 *  4. Capture with html-to-image and trigger a file download.
 *
 * Note: we intentionally do NOT use useReactFlow() here because Toolbar lives
 * outside ReactFlowProvider. Instead, the caller passes `nodes` from the store.
 */

import { toPng, toJpeg, toSvg } from 'html-to-image';
import { getNodesBounds, getViewportForBounds } from 'reactflow';

// Fraction of the content size used as whitespace border (5% each side)
const PADDING_FRACTION = 0.05;

/**
 * Core capture logic shared by all formats.
 * @param {'png'|'jpeg'|'svg'} format
 * @param {Array}  nodes      – nodes array from the Zustand store
 * @param {string} modelName
 * @param {object} [options]
 * @param {string}  [options.backgroundColor]
 * @param {number}  [options.quality]  – jpeg quality 0–1 (default 0.95)
 */
export const exportToImage = async (format, nodes, modelName, options = {}) => {
  if (!nodes || nodes.length === 0) {
    throw new Error('No content to export – the canvas is empty.');
  }

  const bounds = getNodesBounds(nodes);

  // Output image dimensions match the content at 1:1 scale so the viewport
  // zoom stays ≈ 1 and stroke widths render at their normal visual size.
  // High resolution is achieved via pixelRatio: 2 in html-to-image (retina).
  const imageWidth  = Math.max(Math.ceil(bounds.width  * (1 + PADDING_FRACTION * 2)), 800);
  const imageHeight = Math.max(Math.ceil(bounds.height * (1 + PADDING_FRACTION * 2)), 600);

  // getViewportForBounds: last arg is padding as a FRACTION (0.05 = 5%).
  const viewport = getViewportForBounds(bounds, imageWidth, imageHeight, 0.1, 10, PADDING_FRACTION);

  const viewportEl = document.querySelector('.react-flow__viewport');
  if (!viewportEl) throw new Error('React Flow viewport element not found.');

  const bgColor = options.backgroundColor ?? (format === 'svg' ? 'transparent' : '#ffffff');

  const captureOptions = {
    backgroundColor: bgColor,
    width: imageWidth,
    height: imageHeight,
    style: {
      transform: `translate(${viewport.x}px, ${viewport.y}px) scale(${viewport.zoom})`,
      transformOrigin: 'top left',
      width:  `${imageWidth}px`,
      height: `${imageHeight}px`,
    },
    // pixelRatio: 2 doubles physical pixel density (retina) without changing
    // the zoom transform, so stroke widths stay at their normal visual size.
    pixelRatio: 2,
  };

  // Temporarily remove all shadow/filter effects on edges so they render
  // cleanly in the exported image (React Flow adds drop-shadow filters).
  const overrideStyle = document.createElement('style');
  overrideStyle.id = '__export-edge-override';
  overrideStyle.textContent = `
    .react-flow__edge-path,
    .react-flow__edge-interaction,
    .react-flow__edge path,
    .react-flow__edge svg,
    .react-flow__edge {
      filter: none !important;
      -webkit-filter: none !important;
    }
  `;
  document.head.appendChild(overrideStyle);

  let dataUrl;
  try {
    if (format === 'png') {
      dataUrl = await toPng(viewportEl, captureOptions);
    } else if (format === 'jpeg') {
      dataUrl = await toJpeg(viewportEl, { ...captureOptions, quality: options.quality ?? 0.95 });
    } else if (format === 'svg') {
      dataUrl = await toSvg(viewportEl, captureOptions);
    } else {
      throw new Error(`Unknown format: ${format}`);
    }
  } finally {
    // Always restore original styles
    overrideStyle.remove();
  }

  const ext = format === 'jpeg' ? 'jpg' : format;
  const filename = `${(modelName || 'model').replace(/[^a-z0-9]/gi, '_')}_diagram.${ext}`;
  const link = document.createElement('a');
  link.download = filename;
  link.href = dataUrl;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  return filename;
};

export const exportToPng      = (nodes, name, opts) => exportToImage('png',  nodes, name, opts);
export const exportToJpeg     = (nodes, name, opts) => exportToImage('jpeg', nodes, name, opts);
export const exportToSvgImage = (nodes, name, opts) => exportToImage('svg',  nodes, name, opts);
